import { CloseOutlined, QuestionCircleOutlined, UploadOutlined } from '@ant-design/icons'
import {
  Button,
  Col,
  Form,
  GetProp,
  Input,
  message,
  Row,
  Tooltip,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd'
import lodash, { isEqual } from 'lodash'
import { IBaseFormProps } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import { useAccount } from 'wagmi'

import abiPTO from 'abi/abi-pto-v2.json'
import { AppAdmin } from 'api/app-admin'
import { CONFIRMATIONS_BLOCK, ContractStatusEnum } from 'constants/index'
import { ALLOWED_MIME_TYPES_IMAGE } from 'constants/mime-type'
import { useContractViem } from 'hooks/contract/use-contract-viem'
import usePrevious from 'hooks/use-previous'
import { clientWallet, publicWallet } from 'libs/viem/config'
import { generateIpfsUrl } from 'utils'
import { logError, logInfo } from 'utils/logger'

import GoldenTickets from './golden-tickets'

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

interface IMetadataProps extends IBaseFormProps {
  defaultThumbnail?: string
  tokenizeId?: string
}

const MetadataNFT = ({ form, edit, defaultThumbnail, mode }: IMetadataProps) => {
  const [thumbnail, setThumbnail] = useState('')
  const [singleFile, setSingleFile] = useState<UploadFile[]>([])
  const [isManager, setIsManager] = useState(false)

  const previewThumbnail = usePrevious(defaultThumbnail)

  const { address } = useAccount()

  const contractAddress = form.getFieldValue('contractAddress')

  const scContract = useContractViem(contractAddress, abiPTO)

  /**
   * Func check is managers
   */
  const checkIsManagers = useCallback(async () => {
    if (contractAddress && address && scContract) {
      try {
        const rsContract = await scContract.read.whitelistManagers([address])
        if (rsContract) {
          setIsManager(Boolean(rsContract))
        }
      } catch (error: any) {
        logInfo('error---------------------', error)
      }
    }
  }, [address, contractAddress, scContract])

  useEffect(() => {
    if (defaultThumbnail && !isEqual(previewThumbnail, defaultThumbnail)) {
      setThumbnail(defaultThumbnail)
    }
  }, [defaultThumbnail, previewThumbnail])

  useEffect(() => {
    checkIsManagers()
  }, [contractAddress, scContract, address, checkIsManagers])

  const setBaseURI = async () => {
    const baseURI = form.getFieldValue('metadataUrl')
    if (contractAddress && address && scContract && baseURI) {
      try {
        const { request } = await scContract.simulate.setBaseURI([`${baseURI}/`], {
          account: address,
        })
        const txHash = await clientWallet?.writeContract(request)

        if (txHash) {
          const receiptSetBaseURI = await publicWallet.waitForTransactionReceipt({
            hash: txHash,
            confirmations: CONFIRMATIONS_BLOCK,
          })

          if (receiptSetBaseURI.status === ContractStatusEnum.SUCCESS) {
            message.success('Set base URI metadata successfully')
          }
        }
      } catch (error: any) {
        message.error(error?.message)
        logInfo('error---------------------', error)
      }
    }
  }

  /**
   * Func before upload single file
   * @param file
   * @returns
   */
  const beforeUploadSingle = async (file: FileType) => {
    const isJpgOrPng = ALLOWED_MIME_TYPES_IMAGE.includes(file.type)
    if (!isJpgOrPng) {
      form.setFields([
        {
          name: ['metadata', 'image'],
          errors: ['You can only upload JPG/PNG file!'],
        },
      ])
      return false
    }
    return true
  }

  /**
   * func upload single file
   * @param info
   */
  const handleUploadSingle = async (info: any) => {
    const { file } = info
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await AppAdmin.uploadIPFS(formData)
      if (response.data.success) {
        message.success('File uploaded successfully')
        setThumbnail(response.data.data.cid)
        form.setFieldValue(['metadata', 'image'], response.data.data.cid)
        // onUpload && onUpload(response.data.data);
      }
    } catch (error: any) {
      message.error(error?.message || 'File upload failed')
      logError('Upload error:', error)
    }
  }

  return (
    <fieldset>
      <legend>Metadata</legend>
      <Row gutter={24}>
        {mode === 'edit' ? (
          <Col span={24}>
            <Row gutter={24}>
              <Col span={16}>
                <Form.Item name="metadataUrl" label={'Metadata Url'}>
                  <Input
                    style={{ color: 'rgba(0, 0, 0, 0.85)' }}
                    disabled={true}
                    placeholder="Enter the metadata"
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                {isManager ? (
                  <Button
                    style={{ marginTop: 30 }}
                    htmlType="button"
                    disabled={!edit}
                    onClick={() => {
                      setBaseURI()
                    }}
                  >
                    Add to contract
                  </Button>
                ) : null}
              </Col>
            </Row>
          </Col>
        ) : null}

        <Col span={24}>
          <Form.Item
            name={['metadata', 'name']}
            label={'Metadata name'}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const metadata = getFieldValue('metadata')
                  const hasAny = !!(metadata.name || metadata.description || metadata.image)
                  // Nếu có ít nhất 1 trường có dữ liệu => các trường khác phải required
                  if (hasAny && !value) {
                    return Promise.reject(new Error('Metadata name is required!'))
                  }
                  return Promise.resolve()
                },
              }),
            ]}
          >
            <Input
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              disabled={!edit}
              placeholder="Enter the metadata name"
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name={['metadata', 'description']}
            label={'Metadata description'}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const metadata = getFieldValue('metadata') || {}
                  const hasAny = !!(metadata.name || metadata.description || metadata.image)

                  if (hasAny && !value) {
                    return Promise.reject(new Error('Metadata description is required!'))
                  }

                  return Promise.resolve()
                },
              }),
            ]}
          >
            <Input.TextArea
              style={{ color: 'rgba(0, 0, 0, 0.85)' }}
              disabled={!edit}
              placeholder="Enter the metadata description"
              rows={4}
            />
          </Form.Item>
        </Col>
        <Col style={{ marginBottom: 20, marginTop: 20 }} span={12}>
          <div style={{ marginBottom: 10 }}>
            Upload image metadata
            <Tooltip
              title={
                <div className="tooltip-box">
                  <p className="tooltip-text" style={{ color: '#fff' }}>
                    This is your image metadata. Please upload your chosen image.
                  </p>
                </div>
              }
              placement="right"
            >
              <QuestionCircleOutlined className="tooltip-icon" />
            </Tooltip>
          </div>
          <Col style={{ position: 'relative' }} span={12}>
            {thumbnail && (
              <>
                <img
                  style={{
                    width: '184px',
                    height: '184px',
                    border: '1px solid',
                  }}
                  src={generateIpfsUrl(thumbnail) || ''}
                  alt="thumbnail"
                />
                <CloseOutlined
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: '50%',
                    color: '#000',
                    fontSize: '16px',
                    cursor: 'pointer',
                    background: 'white',
                    borderRadius: '50%',
                    padding: '2px',
                    border: '1px solid',
                    display: edit ? 'unset' : 'none',
                  }}
                  onClick={() => {
                    setThumbnail('')
                    setSingleFile([])
                    form.setFieldValue(['metadata', 'image'], null)
                  }}
                />
              </>
            )}
          </Col>
          <Form.Item
            name={['metadata', 'image']}
            rules={[
              {
                required: false,
              },
              ({ getFieldValue }) => ({
                validator(_) {
                  const metadata = getFieldValue('metadata')

                  const hasAny = metadata.name || metadata.description || metadata.image
                  const hasFile = Array.isArray(singleFile) && singleFile.length > 0
                  const isUploading = singleFile.some((f) => f.status === 'uploading')

                  // 🚫 Nếu đang upload → bỏ qua
                  if (isUploading) {
                    return Promise.resolve()
                  }

                  if (hasAny && !metadata.image) {
                    logInfo('run here---------------------------')
                    return Promise.reject(new Error('Metadata image is required!'))
                  }

                  // 🚫 Nếu chưa nhập gì (metadata rỗng) → không validate
                  if (!hasAny && !hasFile) {
                    return Promise.resolve()
                  }

                  // ✅ Khi upload xong mới validate metadata
                  if (!metadata || lodash.isEmpty(metadata)) {
                    return Promise.reject(new Error('Please input the image metadata!'))
                  }

                  return Promise.resolve()
                },
              }),
            ]}
          >
            <Upload
              disabled={!edit}
              fileList={singleFile}
              customRequest={handleUploadSingle}
              beforeUpload={beforeUploadSingle}
            >
              <Button icon={<UploadOutlined />}>Upload Single File</Button>
            </Upload>
          </Form.Item>
          <p>You can only upload JPG/PNG file.</p>
          <p>The image should have the size of 300x300 px.</p>
        </Col>
      </Row>
      <GoldenTickets form={form} edit={edit} mode={mode} />
    </fieldset>
  )
}

export default MetadataNFT
